import { useState } from 'react'
import type { Article } from '@troc/shared'
import { decotesProposees, eur } from '@troc/shared'
import { api, ErreurApi } from '../api'

/**
 * Le prix de vente d'un article, et le geste qu'on lui accorde.
 *
 * Règle de la boutique : **on vend au prix de l'étiquette**. Les étiquettes ne
 * sont presque jamais refaites, alors une caisse qui démarquerait toute seule
 * annoncerait un prix que le client ne lit nulle part. La démarque prévue au
 * contrat reste à un doigt : c'est le bouton **Auto**, qui applique la décote
 * correspondant à l'ancienneté réelle de la pièce.
 *
 * À côté, la liste déroulante permet de se placer où l'on veut — un client
 * négocie rarement pile au palier du contrat — et de geler le prix d'un article
 * qui ne doit pas se dévaluer.
 */

const DECOTES = decotesProposees()

type ArticleReglable = Pick<
  Article,
  'id' | 'decote' | 'decoteConseillee' | 'decoteManuelle' | 'prixInitialCentimes' | 'demarqueActive'
>

export function NiveauxDemarque({
  article,
  onArticle,
  onErreur,
  compact = false,
  variante = 'cases',
}: {
  article: ArticleReglable
  onArticle: (article: Article) => void
  onErreur: (message: string) => void
  /** Version resserrée, pour les lignes de tableau. */
  compact?: boolean
  /**
   * `cases` : les paliers en grand, pour le comptoir — un doigt, un prix.
   * `liste` : bouton Auto + liste déroulante, pour les tableaux.
   */
  variante?: 'cases' | 'liste'
}) {
  const [envoi, setEnvoi] = useState(false)
  /** Le prix qu'on vient de coller en rayon, saisi en euros. */
  const [prixColle, setPrixColle] = useState('')

  const devaluation = (active: boolean) =>
    api<{ article: Article }>(`/articles/${article.id}/devaluation`, {
      methode: 'POST',
      corps: { active },
    }).then((reponse) => {
      onArticle(reponse.article)
      return reponse.article
    })

  const regler = async (decote: number) => {
    if (envoi) return
    setEnvoi(true)
    try {
      // Un article gelé qu'on remet à un prix doit d'abord retrouver sa
      // dévaluation, sinon la décote posée ne bougerait plus jamais.
      if (!article.demarqueActive) await devaluation(true)
      const reponse = await api<{ article: Article }>(`/articles/${article.id}/demarque`, {
        methode: 'POST',
        corps: { decote },
      })
      onArticle(reponse.article)
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Modification impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  const geler = async () => {
    if (envoi) return
    setEnvoi(true)
    try {
      await devaluation(false)
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Modification impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  /** Prix obtenu à une décote donnée, arrondi à l'euro comme en rayon. */
  const prixA = (decote: number) =>
    Math.round((article.prixInitialCentimes * (1 - decote)) / 100) * 100

  /** « −20 % · 248 € » — le taux et sa conséquence, dans cet ordre. */
  const etiquette = (decote: number) =>
    decote === 0
      ? `Plein tarif · ${eur(prixA(0))}`
      : `−${Math.round(decote * 100)} % · ${eur(prixA(decote))}`

  /**
   * La même chose, resserrée pour une cellule de tableau : le prix d'abord,
   * la mention ensuite. C'est le prix qu'on cherche, et c'est lui qui doit
   * survivre si la colonne rétrécit.
   */
  const etiquetteCourte = (decote: number) =>
    decote === 0 ? `${eur(prixA(0))} · plein` : `${eur(prixA(decote))} · −${Math.round(decote * 100)} %`

  const conseillee = article.decoteConseillee
  const gele = !article.demarqueActive
  // Le bouton Auto est allumé quand le prix appliqué est exactement celui que
  // l'ancienneté commande — ni un geste plus large, ni le plein tarif.
  const surAuto = !gele && conseillee > 0 && Math.abs(article.decote - conseillee) < 0.001

  const boutonAuto = (
    <button
      type="button"
      className={surAuto ? 'btn-auto actif' : 'btn-auto'}
      aria-pressed={surAuto}
      disabled={envoi || gele || conseillee === 0}
      // Deuxième appui : on revient au prix de l'étiquette. Le vendeur accorde
      // le geste, le client hésite, il le reprend — sans chercher dans la liste.
      onClick={() => void regler(surAuto ? 0 : conseillee)}
      title={
        gele
          ? 'Cet article ne se dévalue pas'
          : conseillee === 0
            ? 'Aucune démarque due — l’article est dans son premier mois'
            : surAuto
              ? `Revenir au prix de l’étiquette — ${eur(prixA(0))}`
              : `Appliquer la démarque due à son ancienneté : −${Math.round(conseillee * 100)} % · ${eur(prixA(conseillee))}`
      }
    >
      Auto
      {conseillee > 0 && <span className="taux">−{Math.round(conseillee * 100)} %</span>}
    </button>
  )

  if (variante === 'liste') {
    return (
      <div className="reglage-prix">
        {boutonAuto}
        <select
          className="saisie saisie-compacte"
          aria-label="Prix de vente"
          value={gele ? 'gele' : String(article.decote)}
          disabled={envoi}
          onChange={(e) => {
            if (e.target.value === 'gele') void geler()
            else void regler(Number(e.target.value))
          }}
        >
          {DECOTES.map((decote) => (
            <option key={decote} value={decote}>
              {etiquetteCourte(decote)}
              {Math.abs(decote - conseillee) < 0.001 && decote > 0 ? ' · dû' : ''}
            </option>
          ))}
          {/* La « dévaluation progressive » du logiciel actuel, décochée. */}
          <option value="gele">{eur(prixA(0))} · gelé</option>
        </select>
      </div>
    )
  }

  /**
   * Le prix qu'on vient d'écrire sur l'étiquette.
   *
   * Les paliers vont par dix pour cent, ce qui suffit au comptoir mais pas à la
   * tournée de démarque : là, on décide « celui-ci, je le mets à 35 » sur une
   * pièce à 47, et personne ne va convertir en pourcentage. On saisit donc le
   * prix, la décote s'en déduit.
   */
  const poserPrix = async () => {
    const nettoye = prixColle.replace(/[€\s ]/g, '').replace(',', '.')
    const valeur = Number.parseFloat(nettoye)
    if (!Number.isFinite(valeur) || valeur <= 0) {
      onErreur('Indiquez le prix écrit sur l’étiquette.')
      return
    }
    const centimes = Math.round(valeur * 100)
    if (centimes > article.prixInitialCentimes) {
      onErreur(`Le prix ne peut pas dépasser celui du dépôt — ${eur(article.prixInitialCentimes)}.`)
      return
    }
    await regler(
      Math.round((1 - centimes / article.prixInitialCentimes) * 10_000) / 10_000,
    )
    setPrixColle('')
  }

  return (
    <div className="reglage-demarque">
    <div
      className={compact ? 'paliers paliers-compact' : 'paliers'}
      role="group"
      aria-label="Prix de vente"
    >
      {boutonAuto}
      {DECOTES.map((decote) => (
        <button
          key={decote}
          type="button"
          className={
            Math.abs(decote - conseillee) < 0.001 && decote > 0 ? 'palier palier-du' : 'palier'
          }
          aria-pressed={!gele && Math.abs(article.decote - decote) < 0.001}
          disabled={envoi}
          onClick={() => void regler(decote)}
          title={etiquette(decote)}
        >
          <span className="prix">{eur(prixA(decote))}</span>
          <span className="pourcent">{decote === 0 ? 'étiquette' : `−${Math.round(decote * 100)}%`}</span>
        </button>
      ))}
    </div>

      {!compact && (
        <div className="prix-colle">
          <label htmlFor={`prix-colle-${article.id}`} className="etiquette">
            Prix écrit sur la nouvelle étiquette
          </label>
          <input
            id={`prix-colle-${article.id}`}
            className="saisie"
            inputMode="decimal"
            placeholder={eur(prixA(conseillee || 0))}
            value={prixColle}
            disabled={envoi}
            onChange={(e) => setPrixColle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void poserPrix()
              }
            }}
          />
          <button type="button" className="btn" disabled={envoi} onClick={() => void poserPrix()}>
            Poser ce prix
          </button>
          <button
            type="button"
            className="btn btn-fantome"
            disabled={envoi || gele}
            onClick={() => void geler()}
            title="Le prix de cet article ne descendra plus tout seul"
          >
            Geler le prix
          </button>
        </div>
      )}
    </div>
  )
}
